<template>
  <section
    class="counter-section section-padding pt-60 pb-50"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <div class="container">
      <div class="row gx-5">
        <div v-for="(count, index) in counts" :key="index" class="col-lg-3 col-md-3 col-sm-6">
          <div class="single-counter-box">
            <h1 class="counter-number">
              <span class="purecounter" data-purecounter-duration="1" :data-purecounter-end="count.number">
                {{ count.number }} </span
              ><span v-if="count.suffix" class="suffix">{{ count.suffix }}</span>
            </h1>
            <h5 :style="{ color: vars.textColor }">{{ count.title }}</h5>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';

export default defineNuxtComponent({
  name: 'CountSection',
  mixins: [ComponentMixin],

  data() {
    return {
      observers: [] as IntersectionObserver[],
      animatedElements: new Set(),
    };
  },

  computed: {
    counts() {
      return this.groupedVariables.counts;
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.setupIntersectionObservers();
    });
  },

  beforeUnmount() {
    this.observers.forEach((observer) => observer.disconnect());
  },

  methods: {
    setupIntersectionObservers() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      };

      const elements = this.$refs.countElements;
      if (!elements) return;

      (elements as Element[]).forEach((element, index) => {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.animatedElements.has(index)) {
              this.updateCount(index);
              this.animatedElements.add(index);
              observer.unobserve(entry.target);
            }
          });
        }, options);

        observer.observe(element);
        this.observers.push(observer);
      });
    },

    updateCount(index: number) {
      const count = this.counts[index];
      const element = this.$el.querySelectorAll('.odometer')[index];
      if (element) {
        this.animateValue(element, 0, parseInt(count.title, 10), 2000, count.prefix || '', count.suffix || '');
      }
    },

    animateValue(element: HTMLElement, start: number, end: number, duration: number, prefix = '', suffix = '') {
      let startTime: number | null = null;

      const easeOutQuad = (t: number) => {
        return t * (2 - t);
      };

      const step = (timestamp: number) => {
        if (!startTime) {
          startTime = timestamp;
        }
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutQuad(progress);

        const currentValue = Math.floor(easedProgress * (end - start) + start).toLocaleString();

        element.innerText = `${prefix}${currentValue}${suffix}`;

        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    },
  },
});
</script>
